/**
 * Sheet Music Viewer Component
 * Uses OpenSheetMusicDisplay (OSMD) - Open Source Library
 * Displays professional music notation with all symbols
 */

import React, { useEffect, useRef, useState } from 'react';
import { ZoomIn, ZoomOut, Download, Eye, EyeOff } from 'lucide-react';

const SheetMusicViewer = ({ musicXML, title = 'Music Sheet' }) => {
  const containerRef = useRef(null);
  const [osmd, setOsmd] = useState(null);
  const [zoom, setZoom] = useState(1.0);
  const [showParts, setShowParts] = useState({
    fasi: true,
    kanokano: true,
    tenoa: true,
    laulalo: true
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!musicXML || !containerRef.current) return;

    // Dynamically load OpenSheetMusicDisplay
    // In production, install: npm install opensheetmusicdisplay
    loadOpenSheetMusicDisplay();
  }, [musicXML]);

  /**
   * Load OSMD library (open source)
   */
  const loadOpenSheetMusicDisplay = async () => {
    try {
      setLoading(true);
      
      // Check if OSMD is available (loaded via CDN or npm)
      if (typeof window.opensheetmusicdisplay !== 'undefined') {
        const { OpenSheetMusicDisplay } = window.opensheetmusicdisplay;
        initializeOSMD(OpenSheetMusicDisplay);
      } else {
        // Load from CDN if not available
        await loadOSMDFromCDN();
      }
    } catch (err) {
      setError('Failed to load music notation renderer: ' + err.message);
      setLoading(false);
    }
  };

  /**
   * Load OSMD from CDN
   */
  const loadOSMDFromCDN = () => {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/opensheetmusicdisplay@1.8.5/build/opensheetmusicdisplay.min.js';
      script.async = true;
      
      script.onload = () => {
        if (window.opensheetmusicdisplay) {
          const { OpenSheetMusicDisplay } = window.opensheetmusicdisplay;
          initializeOSMD(OpenSheetMusicDisplay);
          resolve();
        } else {
          reject(new Error('OSMD not available'));
        }
      };
      
      script.onerror = () => reject(new Error('Failed to load OSMD library'));
      document.head.appendChild(script);
    });
  };

  /**
   * Initialize OpenSheetMusicDisplay
   */
  const initializeOSMD = async (OpenSheetMusicDisplay) => {
    try {
      // Clear container
      containerRef.current.innerHTML = '';

      // Create OSMD instance
      const osmdInstance = new OpenSheetMusicDisplay(containerRef.current, {
        autoResize: true,
        backend: 'svg',
        drawTitle: true,
        drawComposer: true,
        drawPartNames: true,
        drawFingerings: true,
        coloringMode: 0, // XML coloring
        defaultColorNotehead: '#000000',
        defaultColorRest: '#000000',
        defaultColorStem: '#000000'
      });

      // Load MusicXML
      await osmdInstance.load(musicXML);
      
      // Render
      osmdInstance.render();
      
      setOsmd(osmdInstance);
      setLoading(false);
      setError(null);
    } catch (err) {
      setError('Failed to render music: ' + err.message);
      setLoading(false);
    }
  };

  /**
   * Handle zoom
   */
  const handleZoom = (delta) => {
    const newZoom = Math.max(0.5, Math.min(2.0, zoom + delta));
    setZoom(newZoom);
    
    if (osmd) {
      osmd.zoom = newZoom;
      osmd.render();
    }
  };

  /**
   * Toggle voice part visibility
   */
  const togglePart = (partName) => {
    setShowParts(prev => ({
      ...prev,
      [partName]: !prev[partName]
    }));
    
    // Re-render with updated visibility
    // Note: OSMD implementation would go here
  };

  /**
   * Download as PNG
   */
  const downloadAsPNG = () => {
    if (!containerRef.current) return;

    const svg = containerRef.current.querySelector('svg');
    if (!svg) return;

    // Convert SVG to canvas then to PNG
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${title}.png`;
        a.click();
        URL.revokeObjectURL(url);
      });
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading music notation renderer...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <p className="text-red-800 font-medium">Error loading sheet music:</p>
        <p className="text-red-700 text-sm mt-2">{error}</p>
        <p className="text-gray-600 text-sm mt-4">
          Note: This component requires OpenSheetMusicDisplay (OSMD) library.
          Install it with: <code className="bg-gray-100 px-2 py-1 rounded">npm install opensheetmusicdisplay</code>
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        
        <div className="flex items-center gap-2">
          {/* Zoom Controls */}
          <button
            onClick={() => handleZoom(-0.1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Zoom Out"
          >
            <ZoomOut className="w-5 h-5 text-gray-700" />
          </button>
          
          <span className="text-sm text-gray-600 min-w-[4rem] text-center">
            {Math.round(zoom * 100)}%
          </span>
          
          <button
            onClick={() => handleZoom(0.1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Zoom In"
          >
            <ZoomIn className="w-5 h-5 text-gray-700" />
          </button>

          {/* Download */}
          <button
            onClick={downloadAsPNG}
            className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Download PNG
          </button>
        </div>
      </div>

      {/* Voice Part Toggles */}
      <div className="flex gap-3 mb-4">
        {[
          { id: 'fasi', label: 'Fasi', color: 'blue' },
          { id: 'kanokano', label: 'Kānokano', color: 'green' },
          { id: 'tenoa', label: 'Tēnoa', color: 'yellow' },
          { id: 'laulalo', label: 'Laulalo', color: 'red' }
        ].map(part => (
          <button
            key={part.id}
            onClick={() => togglePart(part.id)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              showParts[part.id]
                ? `bg-${part.color}-100 text-${part.color}-700 border border-${part.color}-300`
                : 'bg-gray-100 text-gray-500 border border-gray-200'
            }`}
          >
            {showParts[part.id] ? (
              <Eye className="w-4 h-4" />
            ) : (
              <EyeOff className="w-4 h-4" />
            )}
            {part.label}
          </button>
        ))}
      </div>

      {/* Sheet Music Container */}
      <div 
        ref={containerRef}
        className="border border-gray-300 rounded-lg p-4 bg-white overflow-auto"
        style={{ minHeight: '400px' }}
      />

      {/* Info Footer */}
      <div className="mt-4 pt-4 border-t border-gray-200 text-sm text-gray-600">
        <p>
          <strong>Powered by:</strong> OpenSheetMusicDisplay (OSMD) - Open Source Music Notation Renderer
        </p>
        <p className="mt-1 text-xs">
          Learn more: <a href="https://opensheetmusicdisplay.org" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">opensheetmusicdisplay.org</a>
        </p>
      </div>
    </div>
  );
};

export default SheetMusicViewer;